import React, { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { adminApi } from '../services/api';
import './AdminPanel.css';

/**
 * AdminPanel — self-service access requests and administrator request review.
 * Rendered in the sidebar beneath the NodeDetail control.
 */
interface AdminPanelProps {
  isAdmin: boolean;
}

type ReasonAction =
  | { kind: 'deny-request'; requestID: string; label: string }
  | { kind: 'revoke-role'; subject: string; role: string; label: string };

type AccessRequest = {
  request_id: string;
  subject: string;
  display_name?: string;
  email: string;
  role: string;
  status: string;
  requested_at: string;
  decided_at?: string;
  decided_by?: string;
  decision_reason?: string;
};

type AccessRequestGroup = {
  key: string;
  identity: AccessRequest;
  requests: AccessRequest[];
};

type AuthorizationHistoryEntry = {
  subject: string;
  display_name?: string;
  record_kind: string;
  role?: string;
  organization_id?: string;
  status: string;
  actor?: string;
  reason?: string;
  occurred_at: string;
};

const groupAccessRequests = (requests: AccessRequest[]): AccessRequestGroup[] => {
  const groups = new Map<string, AccessRequestGroup>();
  requests.forEach((request) => {
    const key = request.subject || request.email;
    const existing = groups.get(key);
    if (existing) {
      existing.requests.push(request);
      return;
    }
    groups.set(key, { key, identity: request, requests: [request] });
  });
  return Array.from(groups.values());
};

const AdminPanel: React.FC<AdminPanelProps> = ({ isAdmin }) => {
  const { permissions } = useAuthContext();
  const [status, setStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [users, setUsers] = useState<Array<{ subject: string; display_name: string; email: string; roles: string[]; organization_ids: string[] }>>([]);
  const [pendingRequests, setPendingRequests] = useState<AccessRequest[]>([]);
  const [requestHistory, setRequestHistory] = useState<AccessRequest[]>([]);
  const [authorizationHistory, setAuthorizationHistory] = useState<AuthorizationHistoryEntry[]>([]);
  const [usersOpen, setUsersOpen] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);
  const [requestHistoryOpen, setRequestHistoryOpen] = useState(false);
  const [requestRolesOpen, setRequestRolesOpen] = useState(false);
  const [pendingRequestedRoles, setPendingRequestedRoles] = useState<string[]>([]);
  const [roleMenuSubject, setRoleMenuSubject] = useState<string | null>(null);
  const [reasonAction, setReasonAction] = useState<ReasonAction | null>(null);
  const [reasonValue, setReasonValue] = useState('');
  const [processingRequestIDs, setProcessingRequestIDs] = useState<Set<string>>(new Set());
  const [focusRequestID, setFocusRequestID] = useState<string | null>(null);
  const requestButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const refreshButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!message) {
      return undefined;
    }
    const timeoutID = window.setTimeout(() => {
      setMessage(null);
      setStatus('idle');
    }, 4000);
    return () => window.clearTimeout(timeoutID);
  }, [message]);

  useEffect(() => {
    if (!focusRequestID) {
      return;
    }
    const target = requestButtonRefs.current[focusRequestID] || refreshButtonRef.current;
    target?.focus();
    setFocusRequestID(null);
  }, [focusRequestID, pendingRequests]);

  const loadUsers = async (): Promise<void> => {
    setUsersLoading(true);
    try {
      setUsers(await adminApi.listAuthorizationUsers());
      setUsersLoaded(true);
    } catch (err) {
      setMessage((err as Error).message || 'Could not load users.');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadPendingRequests = async (showLoading = true): Promise<void> => {
    if (showLoading) {
      setPendingRequestsLoading(true);
    }
    try {
      const page = await adminApi.listAccessRequests('pending');
      setPendingRequests(page.data);
    } catch (err) {
      setMessage((err as Error).message || 'Could not load access requests.');
    } finally {
      if (showLoading) {
        setPendingRequestsLoading(false);
      }
    }
  };

  const loadRequestHistory = async (): Promise<void> => {
    try {
      const [requestPage, lifecyclePage] = await Promise.all([
        adminApi.listAccessRequests('all'),
        adminApi.listAuthorizationHistory(),
      ]);
      setRequestHistory(requestPage.data);
      setAuthorizationHistory(lifecyclePage);
    } catch (err) { setMessage((err as Error).message || 'Could not load authorization history.'); }
  };

  const decideRequest = async (requestID: string, granted: boolean, decisionReason = ''): Promise<void> => {
    setProcessingRequestIDs((current) => new Set(current).add(requestID));
    try {
      await adminApi.decideAccessRequest(requestID, granted, decisionReason);
      const currentIndex = pendingRequests.findIndex((request) => request.request_id === requestID);
      const nextRequest = pendingRequests[currentIndex + 1] || pendingRequests[currentIndex - 1];
      setPendingRequests((requests) => requests.filter((request) => request.request_id !== requestID));
      setFocusRequestID(nextRequest?.request_id || '__refresh__');
      await Promise.all([
        loadPendingRequests(false),
        usersLoaded ? loadUsers() : Promise.resolve(),
        requestHistoryOpen ? loadRequestHistory() : Promise.resolve(),
      ]);
      setReasonAction(null);
      setReasonValue('');
      setStatus('done');
      setMessage(granted ? 'Access request approved.' : 'Access request denied.');
    } catch (err) {
      setStatus('error');
      setMessage((err as Error).message || 'Could not decide access request.');
    } finally {
      setProcessingRequestIDs((current) => {
        const next = new Set(current);
        next.delete(requestID);
        return next;
      });
    }
  };

  const openReasonForm = (action: ReasonAction): void => {
    setRoleMenuSubject(null);
    setReasonAction(action);
    setReasonValue('');
    setMessage(null);
  };

  const cancelReasonForm = (): void => {
    setReasonAction(null);
    setReasonValue('');
  };

  const revokeUserRole = async (subject: string, role: string, revocationReason: string): Promise<void> => {
    try {
      await adminApi.setAuthorizationRole(subject, role, false, revocationReason);
      await loadUsers();
      setReasonAction(null);
      setReasonValue('');
      setStatus('done');
      setMessage(`${role} role removed.`);
    } catch (err) {
      setStatus('error');
      setMessage((err as Error).message || 'Could not revoke user access.');
    }
  };

  const submitReasonForm = async (): Promise<void> => {
    if (!reasonAction) {
      return;
    }

    const trimmedReason = reasonValue.trim();
    if (!trimmedReason) {
      setStatus('error');
      setMessage('A reason is required.');
      return;
    }

    if (reasonAction.kind === 'deny-request') {
      await decideRequest(reasonAction.requestID, false, trimmedReason);
    } else if (reasonAction.kind === 'revoke-role') {
      await revokeUserRole(reasonAction.subject, reasonAction.role, trimmedReason);
    }
  };

  const requestRole = async (role: string): Promise<void> => {
    setMessage(null);
    try {
      await adminApi.requestAuthorizationRole(role);
      setPendingRequestedRoles((roles) => roles.includes(role) ? roles : [...roles, role]);
      setStatus('done');
      setMessage(`${role} access request submitted.`);
    } catch (err) {
      setStatus('error');
      setMessage((err as Error).message || 'Could not submit access request.');
    }
  };

  const requestableRoles = [
    { value: 'curator', label: 'Curator', description: 'Can approve/reject Proposer proposals. Both create citations to provide lineage for the ontology.' },
    { value: 'proposer', label: 'Proposer', description: 'Can upload source materials and trigger analysis to generate proposals to the graph.' },
    { value: 'admin', label: 'Administrator', description: 'Role management and other system-level operations.' },
  ].filter((role) => !permissions.includes(role.value));
  const decidedRequestHistory = requestHistory.filter((request) => request.status !== 'pending');
  const historyItems = [
    ...decidedRequestHistory.map((request) => ({
      key: `request-${request.request_id}`,
      subject: request.subject,
      displayName: request.display_name || request.subject,
      recordKind: 'request',
      role: request.role,
      organizationID: '',
      status: request.status,
      reason: request.decision_reason,
      occurredAt: request.decided_at || request.requested_at,
    })),
    ...authorizationHistory
      .filter((entry) => entry.status === 'revoked')
      .map((entry) => ({
        key: `authorization-${entry.record_kind}-${entry.subject}-${entry.role || entry.organization_id}-${entry.occurred_at}`,
        subject: entry.subject,
        displayName: entry.display_name || entry.subject,
        recordKind: entry.record_kind,
        role: entry.role || '',
        organizationID: entry.organization_id || '',
        status: entry.status,
        reason: entry.reason,
        occurredAt: entry.occurred_at,
      })),
  ].sort((left, right) => Date.parse(right.occurredAt) - Date.parse(left.occurredAt));

  useEffect(() => {
    if (!isAdmin) {
      void adminApi.listMyAccessRequests()
        .then((page) => setPendingRequestedRoles(page.data.map((request) => request.role)))
        .catch(() => setPendingRequestedRoles([]));
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      void loadPendingRequests();
    }
  }, [isAdmin]);

  const toggleUsers = (): void => {
    const nextOpen = !usersOpen;
    setUsersOpen(nextOpen);
    if (nextOpen) {
      setRequestHistoryOpen(false);
    }
    if (nextOpen && !usersLoaded) {
      void loadUsers();
    }
  };

  const toggleRequestHistory = (): void => {
    const nextOpen = !requestHistoryOpen;
    setRequestHistoryOpen(nextOpen);
    if (nextOpen) {
      setUsersOpen(false);
      void loadRequestHistory();
    }
  };

  return (
    <div className="admin-panel">
      <h4 className="admin-panel__title">{isAdmin ? 'Access administration' : 'Request access'}</h4>

    {message && (
      <div className={`admin-panel__toast admin-panel__toast--${status}`} role={status === 'error' ? 'alert' : 'status'} aria-live="polite">
        <span>{message}</span>
        <button className="admin-panel__toast-dismiss" type="button" onClick={() => setMessage(null)} aria-label="Dismiss notification">x</button>
      </div>
    )}

    {!isAdmin && <div className="admin-panel__request-role">
      <button className="btn-secondary admin-panel__btn" onClick={() => setRequestRolesOpen((open) => !open)} aria-expanded={requestRolesOpen} aria-controls="requestable-roles">
        Request role
      </button>
      {requestRolesOpen && (
        <div id="requestable-roles" className="admin-panel__role-menu" role="dialog" aria-label="Available roles to request">
          {requestableRoles.map((role) => (
            <div className="admin-panel__role-option" key={role.value}>
              <span className="admin-panel__role-term" tabIndex={0} aria-describedby={`role-description-${role.value}`}>
                {role.label}
                <span id={`role-description-${role.value}`} className="admin-panel__role-tooltip" role="tooltip">{role.description}</span>
              </span>
              <button
                className="btn-secondary admin-panel__btn"
                onClick={() => void requestRole(role.value)}
                disabled={pendingRequestedRoles.includes(role.value)}
                title={pendingRequestedRoles.includes(role.value) ? 'Request pending' : undefined}
              >
                {pendingRequestedRoles.includes(role.value) ? 'Pending' : 'Request'}
              </button>
            </div>
          ))}
          {requestableRoles.length === 0 && <p className="admin-panel__empty">No additional roles available.</p>}
        </div>
      )}
    </div>}

    {isAdmin && <>
      <section className="admin-panel__section admin-panel__requests" aria-labelledby="pending-access-requests">
        <div className="admin-panel__section-header">
          <h5 id="pending-access-requests">Pending access requests</h5>
          <button ref={refreshButtonRef} className="btn-secondary admin-panel__btn" onClick={() => void loadPendingRequests()} disabled={pendingRequestsLoading}>
            {pendingRequestsLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        {pendingRequestsLoading && <p className="admin-panel__empty">Loading access requests...</p>}
        {!pendingRequestsLoading && pendingRequests.length === 0 && (
          <p className="admin-panel__empty">No pending access requests.</p>
        )}
        {!pendingRequestsLoading && groupAccessRequests(pendingRequests).map((group) => (
          <article className="admin-panel__request" key={group.key}>
            <div className="admin-panel__request-row admin-panel__request-identity">
              <span><strong>Name:</strong> {group.identity.display_name || group.identity.subject}</span>
              <span><strong>Email:</strong> {group.identity.email || 'Not provided'}</span>
            </div>
            <div className="admin-panel__request-access">
              {group.requests.map((request) => (
                <div className="admin-panel__request-role" key={request.request_id}>
                  <div className="admin-panel__request-row">
                    <div className="admin-panel__request-details">
                      <span><strong>Requested role:</strong> {request.role}</span>
                    </div>
                    <div className="admin-panel__request-actions">
                      <button ref={(element) => { requestButtonRefs.current[request.request_id] = element; }} className="btn-secondary admin-panel__btn admin-panel__request-btn--approve" onClick={() => void decideRequest(request.request_id, true)} disabled={processingRequestIDs.has(request.request_id)}>Approve</button>
                      <button className="btn-secondary admin-panel__btn admin-panel__request-btn--deny" onClick={() => openReasonForm({ kind: 'deny-request', requestID: request.request_id, label: request.email || request.subject })} disabled={processingRequestIDs.has(request.request_id)}>Deny</button>
                    </div>
                  </div>
                  {reasonAction?.kind === 'deny-request' && reasonAction.requestID === request.request_id && (
                    <div className="admin-panel__deny-form" role="dialog" aria-label={`Deny access request from ${reasonAction.label}`}>
                      <textarea
                        className="admin-panel__deny-input"
                        rows={3}
                        autoFocus
                        value={reasonValue}
                        onChange={(event) => setReasonValue(event.target.value)}
                        placeholder="Reason for denying this request"
                      />
                      <div className="admin-panel__deny-actions">
                        <button className="btn-secondary admin-panel__btn" onClick={cancelReasonForm}>Cancel</button>
                        <button className="btn-danger admin-panel__btn" onClick={() => void submitReasonForm()} disabled={!reasonValue.trim()}>Deny request</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </article>
        ))}
      </section>

      <section className="admin-panel__section admin-panel__management">
        <div className="admin-panel__management-row">
          <button
            className="btn-secondary admin-panel__btn admin-panel__manage-users"
            onClick={toggleUsers}
            aria-expanded={usersOpen}
            aria-controls="admin-user-list"
          >
            Manage users
          </button>
          <button
            className="btn-secondary admin-panel__btn"
            onClick={toggleRequestHistory}
            aria-expanded={requestHistoryOpen}
          >
            Request history
          </button>
        </div>
        {usersOpen && (
          <div id="admin-user-list" className="admin-panel__user-list">
            {usersLoading && <p className="admin-panel__empty">Loading users...</p>}
            {!usersLoading && users.length === 0 && <p className="admin-panel__empty">No users found.</p>}
            {!usersLoading && users.map((user) => (
              <div className="admin-panel__user" key={user.subject}>
                <div className="admin-panel__user-header">
                  <div className="admin-panel__user-identity" title={user.subject}>{user.display_name || user.email || user.subject}</div>
                  {user.roles.some((role) => role !== 'viewer') && (
                    <button
                      className="btn-danger admin-panel__btn admin-panel__user-revoke"
                      onClick={() => setRoleMenuSubject((current) => current === user.subject ? null : user.subject)}
                      aria-expanded={roleMenuSubject === user.subject}
                      aria-controls={`admin-role-menu-${user.subject}`}
                    >
                      Remove role
                    </button>
                  )}
                </div>
                <div className="admin-panel__user-meta">
                  <strong>Roles:</strong>{' '}
                  {user.roles.includes('viewer') ? user.roles.join(', ') : [...user.roles, 'viewer'].join(', ')}
                </div>
                <div className="admin-panel__user-meta"><strong>Organizations:</strong> {user.organization_ids.join(', ') || 'none'}</div>
                {roleMenuSubject === user.subject && (
                  <div id={`admin-role-menu-${user.subject}`} className="admin-panel__role-menu" role="dialog" aria-label={`Roles for ${user.display_name || user.email || user.subject}`}>
                    {[...user.roles.filter((role) => role !== 'viewer'), 'viewer'].map((role) => (
                      <div className="admin-panel__role-option" key={role}>
                        <span>{role}</span>
                        {role !== 'viewer' && (
                          <button
                            className="btn-danger admin-panel__btn"
                            onClick={() => openReasonForm({ kind: 'revoke-role', subject: user.subject, role, label: user.display_name || user.email || user.subject })}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {reasonAction?.kind === 'revoke-role' && reasonAction.subject === user.subject && (
                  <div className="admin-panel__deny-form" role="dialog" aria-label={`Remove ${reasonAction.role} access for ${reasonAction.label}`}>
                    <textarea
                      className="admin-panel__deny-input"
                      rows={3}
                      autoFocus
                      value={reasonValue}
                      onChange={(event) => setReasonValue(event.target.value)}
                      placeholder={`Reason for removing the ${reasonAction.role} role`}
                    />
                    <div className="admin-panel__deny-actions">
                      <button className="btn-secondary admin-panel__btn" onClick={cancelReasonForm}>Cancel</button>
                      <button className="btn-danger admin-panel__btn" onClick={() => void submitReasonForm()} disabled={!reasonValue.trim()}>Remove role</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {requestHistoryOpen && (
          <div className="admin-panel__history">
            {historyItems.length === 0 && <p className="admin-panel__empty">No request history found.</p>}
            {historyItems.map((entry) => (
              <div className="admin-panel__decision" key={entry.key}>
                <span>
                  <strong>{entry.displayName}</strong>
                  {entry.recordKind === 'request' && <>{' requested '}{entry.role}: <strong>{entry.status}</strong></>}
                  {entry.recordKind === 'role' && <>{' lost the '}{entry.role} role: <strong>{entry.status}</strong></>}
                  {entry.recordKind === 'membership' && <>{' lost membership in '}{entry.organizationID}: <strong>{entry.status}</strong></>}
                  {entry.reason && ` (${entry.reason})`}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
		</>}
    </div>
  );
};

export default AdminPanel;
